import { safeParseAndLog } from "@/utils/safeParseAndLog.ts";
import { type SetStateAction, useCallback, useMemo } from "react";
import type { Jsonifiable } from "type-fest";
import type z from "zod";

export type State<T> = [state: T, setState: (state: (prev: T) => T) => void];

/** Wraps a {@link State} with a Zod schema validator */
export function useZodStateParser<T extends Jsonifiable>(
    schema: z.ZodType<T>,
    [state, setState]: State<unknown>,
    fallback: T,
): State<T> {
    const parse = useCallback(
        (state: unknown): T => {
            if (import.meta.env.DEV) {
                // Validate that the fallback matches the schema
                schema.parse(fallback);
            }

            if (state === undefined) {
                return fallback;
            }

            return safeParseAndLog(schema, state, fallback, "State parse error");
        },
        [schema, fallback],
    );

    const parsed = useMemo(() => parse(state), [state, parse]);

    const setParsed = (action: SetStateAction<T>) => {
        setState((p) => {
            const current = parse(p);
            return typeof action === "function" ? action(current) : action;
        });
    };
    return [parsed, setParsed] as const;
}
